import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  PalletStatistics,
  PalletWithDetails,
  palletService,
} from "~/services/wms/palletService";
import type {
  Pallet,
  PalletBatch,
  PalletBatchFormData,
  PalletFilters,
  PalletStatus,
  RelocationResult,
} from "~/types/wms";

// Query keys
const PALLETS_KEY = "wms_pallets";
const PALLET_DETAIL_KEY = "wms_pallet_detail";
const PALLET_STATISTICS_KEY = "wms_pallet_statistics";
const PALLET_MOVEMENTS_KEY = "wms_pallet_movements";

// ============================================================================
// Queries
// ============================================================================

/**
 * Get list of pallets with optional filters
 */
export function usePallets(filters?: PalletFilters) {
  return useQuery({
    queryKey: [PALLETS_KEY, filters],
    queryFn: () => palletService.getPallets(filters),
  });
}

/**
 * Get single pallet by ID with details and batches
 */
export function usePallet(id: string | undefined) {
  return useQuery({
    queryKey: [PALLET_DETAIL_KEY, id],
    queryFn: () => palletService.getPalletById(id!),
    enabled: !!id,
  });
}

/**
 * Get pallet by barcode (for scanning)
 */
export function usePalletByBarcode(barcode: string | undefined) {
  return useQuery({
    queryKey: [PALLETS_KEY, "barcode", barcode],
    queryFn: () => palletService.getPalletByBarcode(barcode!),
    enabled: !!barcode,
  });
}

/**
 * Get pallet by code
 */
export function usePalletByCode(code: string | undefined) {
  return useQuery({
    queryKey: [PALLETS_KEY, "code", code],
    queryFn: () => palletService.getPalletByCode(code!),
    enabled: !!code,
  });
}

/**
 * Search pallets by code or barcode
 */
export function useSearchPallets(query: string, limit = 10) {
  return useQuery({
    queryKey: [PALLETS_KEY, "search", query, limit],
    queryFn: () => palletService.searchPallets(query, limit),
    enabled: query.length >= 2,
  });
}

/**
 * Get pallets at a specific position
 */
export function usePalletsAtPosition(positionId: string | undefined) {
  return useQuery({
    queryKey: [PALLETS_KEY, "position", positionId],
    queryFn: () => palletService.getPalletsAtPosition(positionId!),
    enabled: !!positionId,
  });
}

/**
 * Get pallet statistics
 */
export function usePalletStatistics(warehouseId?: string) {
  return useQuery({
    queryKey: [PALLET_STATISTICS_KEY, warehouseId],
    queryFn: () => palletService.getPalletStatistics(warehouseId),
  });
}

/**
 * Get pallet movement history
 */
export function usePalletMovements(palletId: string | undefined) {
  return useQuery({
    queryKey: [PALLET_MOVEMENTS_KEY, palletId],
    queryFn: () => palletService.getPalletMovements(palletId!),
    enabled: !!palletId,
  });
}

// ============================================================================
// Mutations
// ============================================================================

/**
 * Relocate a pallet to a new position
 */
export function useRelocatePallet() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      palletId,
      toPositionId,
      userId,
    }: {
      palletId: string;
      toPositionId: string;
      userId: string;
    }) => palletService.relocatePallet(palletId, toPositionId, userId),
    onSuccess: (result: RelocationResult) => {
      queryClient.invalidateQueries({ queryKey: [PALLETS_KEY] });
      queryClient.invalidateQueries({
        queryKey: [PALLET_DETAIL_KEY, result.pallet_id],
      });
      queryClient.invalidateQueries({
        queryKey: [PALLET_MOVEMENTS_KEY, result.pallet_id],
      });
      queryClient.invalidateQueries({ queryKey: [PALLET_STATISTICS_KEY] });
      // Also invalidate inventory queries
      queryClient.invalidateQueries({ queryKey: ["wms_inventory"] });
    },
  });
}

/**
 * Add a batch to a pallet (for mixed pallets)
 */
export function useAddBatchToPallet() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: PalletBatchFormData) =>
      palletService.addBatchToPallet(data),
    onSuccess: (batch: PalletBatch) => {
      queryClient.invalidateQueries({ queryKey: [PALLETS_KEY] });
      queryClient.invalidateQueries({
        queryKey: [PALLET_DETAIL_KEY, batch.pallet_id],
      });
      queryClient.invalidateQueries({ queryKey: [PALLET_STATISTICS_KEY] });
    },
  });
}

/**
 * Update box count for a batch on a pallet
 */
export function useUpdateBatchBoxCount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      batchId,
      boxCount,
    }: {
      batchId: string;
      boxCount: number;
    }) => palletService.updateBatchBoxCount(batchId, boxCount),
    onSuccess: (batch: PalletBatch) => {
      queryClient.invalidateQueries({ queryKey: [PALLETS_KEY] });
      queryClient.invalidateQueries({
        queryKey: [PALLET_DETAIL_KEY, batch.pallet_id],
      });
      queryClient.invalidateQueries({ queryKey: [PALLET_STATISTICS_KEY] });
    },
  });
}

/**
 * Remove a batch from a pallet
 */
export function useRemoveBatchFromPallet() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      batchId,
      palletId,
    }: {
      batchId: string;
      palletId: string;
    }) => palletService.removeBatchFromPallet(batchId),
    onSuccess: (_, { palletId }) => {
      queryClient.invalidateQueries({ queryKey: [PALLETS_KEY] });
      queryClient.invalidateQueries({
        queryKey: [PALLET_DETAIL_KEY, palletId],
      });
      queryClient.invalidateQueries({ queryKey: [PALLET_STATISTICS_KEY] });
    },
  });
}

/**
 * Update pallet status
 */
export function useUpdatePalletStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      palletId,
      status,
    }: {
      palletId: string;
      status: PalletStatus;
    }) => palletService.updatePalletStatus(palletId, status),
    onSuccess: (pallet: Pallet) => {
      queryClient.invalidateQueries({ queryKey: [PALLETS_KEY] });
      queryClient.invalidateQueries({
        queryKey: [PALLET_DETAIL_KEY, pallet.id],
      });
      queryClient.invalidateQueries({ queryKey: [PALLET_STATISTICS_KEY] });
    },
  });
}

// ============================================================================
// Utility hooks for scanning workflow
// ============================================================================

/**
 * Look up pallet by barcode or code (for scanning input)
 */
export function useLookupPallet() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: string): Promise<PalletWithDetails | null> => {
      // First try by barcode
      let pallet = await palletService.getPalletByBarcode(input);
      if (pallet) return pallet;

      // Then try by code
      pallet = await palletService.getPalletByCode(input);
      return pallet;
    },
  });
}

/**
 * Look up position by barcode or full code (for destination scanning)
 */
export function useLookupPosition() {
  return useMutation({
    mutationFn: async (input: string) => {
      // First try by barcode
      let position = await palletService.getPositionByBarcode(input);
      if (position) return position;

      // Then try by full code
      position = await palletService.getPositionByFullCode(input);
      return position;
    },
  });
}
